import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWellness } from "../context/WellnessContext";
import { fetchWellnessData } from "@/api/wellness.api";
import { getPeriodDeltaRange } from "@/utils/periodDeltaRanges";
import { useBootstrap } from "@/features/bootstrap";

const PERIOD_ORDER = ["2months", "6months", "1year", "2years", "4years"];

export function useLoadAllWellnessData() {
  const queryClient = useQueryClient();
  const { data: user } = useBootstrap();
  const { engine, loadPeriod } = useWellness();
  const inProgressRef = useRef(false);

  useEffect(() => {
    if (!user?.studentIds?.length) {
      return;
    }
    if (inProgressRef.current) {
      return;
    }

    let cancelled = false;
    inProgressRef.current = true;

    (async () => {
      try {
        for (const period of PERIOD_ORDER) {
          if (cancelled) {
            break;
          }
          if (engine.periods.has(period)) {
            continue;
          }

          const loaderFn = async () => {
            const delta = getPeriodDeltaRange(engine, period);

            if (!delta) {
              return null;
            }

            const { start, end } = delta;
            const startISO = start.toISOString();
            const endISO = end.toISOString();

            const key = ["wellness", period, startISO, endISO];

            const { raw, byUserId } = await queryClient.fetchQuery({
              queryKey: key,
              queryFn: async () =>
                fetchWellnessData(user.studentIds, start, end),
              staleTime: 1000 * 60 * 180,
              cacheTime: 1000 * 60 * 240,
              retry: 1,
            });

            return {
              raw,
              byUserId,
            };
          };

          try {
            await loadPeriod(period, loaderFn);
          } catch (e) {}
        }
      } catch (e) {
      } finally {
        inProgressRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      inProgressRef.current = false;
    };
  }, [user]);
}
